import type { CaptureResult, Properties } from "posthog-js";
import posthog from "posthog-js";
import type { PostHogRuntimeConfig } from "./lib/publicConfig";

interface AnalyticsIdentity {
  user_id: string;
  organization_id: string;
  // A person PROPERTY, never the identity key — `user_id` stays the distinct_id
  // so this can be dropped later without orphaning person history.
  email?: string | null;
  traffic_class: "customer" | "internal" | "synthetic_test";
}

export interface AnalyticsPage {
  url: string;
  component: string;
  props: {
    analytics_identity?: AnalyticsIdentity | null;
    public_config?: { posthog?: PostHogRuntimeConfig };
    [key: string]: unknown;
  };
}

const EU_API_HOST = "https://eu.i.posthog.com";
const BROWSER_API_HOST = "https://t.weftlabs.com";
const EU_UI_HOST = "https://eu.posthog.com";
const LOCATION_PROPERTY_KEYS = new Set([
  "_gl",
  "epik",
  "gad_source",
  "gbraid",
  "li_fat_id",
  "sccid",
  "title",
  "wbraid",
]);
const CROSS_DOMAIN_ID = /^[A-Za-z0-9_-]{16,200}$/;

interface CrossDomainBootstrap {
  distinctID: string;
  sessionID: string;
}

type EnabledPostHogRuntimeConfig = PostHogRuntimeConfig & {
  enabled: true;
  api_key: string;
  api_host: string;
  browser_api_host: string;
  ui_host: string;
};

let initialized = false;
let captureBlocked = false;
let config: PostHogRuntimeConfig | null = null;
let identifiedUserId: string | null = null;
let groupedOrganizationId: string | null = null;
let registeredTrafficClass: AnalyticsIdentity["traffic_class"] | null = null;
let sessionRecordingAllowed: boolean | null = null;
// The class last sent to the PERSON profile, tracked separately from the
// registered super property: the two are written by different SDK calls, so one
// flag cannot tell us both have caught up with a reclassification.
let identifiedTrafficClass: AnalyticsIdentity["traffic_class"] | null = null;

function validConfig(
  candidate: PostHogRuntimeConfig | null | undefined,
): candidate is EnabledPostHogRuntimeConfig {
  return Boolean(
    candidate?.enabled &&
      candidate.api_key &&
      candidate.api_host === EU_API_HOST &&
      candidate.browser_api_host === BROWSER_API_HOST &&
      candidate.ui_host === EU_UI_HOST &&
      ["production", "staging"].includes(candidate.environment) &&
      candidate.surface === "web_app" &&
      Number.isInteger(candidate.schema_version),
  );
}

function takeSignupAnalyticsHandoff(): CrossDomainBootstrap | undefined {
  if (window.location.pathname !== "/signup" || !window.location.hash) {
    return undefined;
  }

  const params = new URLSearchParams(window.location.hash.slice(1));
  if (!params.has("distinct_id") && !params.has("session_id")) {
    return undefined;
  }

  try {
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  } catch {
    return undefined;
  }

  const distinctIds = params.getAll("distinct_id");
  const sessionIds = params.getAll("session_id");
  if (distinctIds.length !== 1 || sessionIds.length !== 1) {
    return undefined;
  }

  const [distinctID] = distinctIds;
  const [sessionID] = sessionIds;
  if (!CROSS_DOMAIN_ID.test(distinctID) || !CROSS_DOMAIN_ID.test(sessionID)) {
    return undefined;
  }

  return { distinctID, sessionID };
}

function eventContext(candidate: PostHogRuntimeConfig) {
  return {
    environment: candidate.environment,
    surface: candidate.surface,
    app_version: candidate.app_version,
    schema_version: candidate.schema_version,
  };
}

function containsLocationData(key: string) {
  const normalized = key.toLowerCase();
  return (
    normalized.includes("url") ||
    normalized.includes("pathname") ||
    normalized.includes("referrer") ||
    normalized.includes("referring_domain") ||
    normalized.includes("keyword") ||
    normalized.startsWith("utm_") ||
    normalized.endsWith("clid") ||
    LOCATION_PROPERTY_KEYS.has(normalized)
  );
}

function withoutLocationData(properties: Properties | undefined) {
  if (!properties) return undefined;

  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => !containsLocationData(key)),
  );
}

export function sanitizeAnalyticsCapture(capture: CaptureResult | null) {
  if (!capture) return null;
  if (capture.event === "$snapshot" && sessionRecordingAllowed === false)
    return null;

  const properties = withoutLocationData(capture.properties) ?? {};
  if (capture.event === "$pageview") {
    const routeComponent =
      typeof capture.properties.route_component === "string"
        ? capture.properties.route_component
        : "unknown";
    const pathname = componentPath(routeComponent);
    properties.route_component =
      pathname === "/:unknown" ? "unknown" : routeComponent;
    properties.$pathname = pathname;
    properties.$current_url = `${window.location.origin}${pathname}`;
  }

  return {
    ...capture,
    properties,
    ...(capture.$set ? { $set: withoutLocationData(capture.$set) } : {}),
    ...(capture.$set_once
      ? { $set_once: withoutLocationData(capture.$set_once) }
      : {}),
  };
}

export function initializeAnalytics(
  candidate: PostHogRuntimeConfig | null | undefined,
  initialIdentity?: AnalyticsIdentity | null,
) {
  if (initialized) return true;
  if (typeof window === "undefined") return false;

  const bootstrap = takeSignupAnalyticsHandoff();
  if (!validConfig(candidate)) return false;

  const allowSessionRecording = !usesWeftLabsEmail(initialIdentity);

  try {
    posthog.init(candidate.api_key, {
      api_host: candidate.browser_api_host,
      ui_host: candidate.ui_host,
      before_send: sanitizeAnalyticsCapture,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_exceptions: false,
      capture_performance: false,
      enable_recording_console_log: false,
      save_campaign_params: false,
      save_referrer: false,
      disable_capture_url_hashes: true,
      disable_scroll_properties: true,
      disable_session_recording: !allowSessionRecording,
      disable_conversations: true,
      // Session replay's recorder.js is an external dependency. Blocking it
      // silently disables replay however the project is configured.
      disable_external_dependency_loading: false,
      disable_product_tours: true,
      disable_surveys: true,
      disable_web_experiments: true,
      // Replay settings share PostHog's remote-config request. Keep that
      // request enabled while the two feature-flag controls below prevent
      // evaluation and first-load flag requests.
      advanced_disable_flags: false,
      advanced_disable_feature_flags: true,
      advanced_disable_feature_flags_on_first_load: true,
      // PostHog drops HeadlessChrome before queueing or sending. Staging QA is
      // browser automation, so allow its named captures there while production
      // keeps the SDK's bot filter.
      opt_out_useragent_filter: candidate.environment === "staging",
      person_profiles: "identified_only",
      ...(bootstrap ? { bootstrap } : {}),
    });
    posthog.register(eventContext(candidate));
    config = candidate;
    sessionRecordingAllowed = allowSessionRecording;
    initialized = true;
    return true;
  } catch {
    return false;
  }
}

function usesWeftLabsEmail(identity: AnalyticsIdentity | null | undefined) {
  return Boolean(
    identity?.email?.trim().toLowerCase().endsWith("@weftlabs.com"),
  );
}

function setSessionRecordingAllowed(allowed: boolean) {
  if (sessionRecordingAllowed === allowed) return true;

  const previousValue = sessionRecordingAllowed;
  // `stopSessionRecording()` can return while compressed replay data is still
  // in flight. Change the final-send policy first so neither a synchronous nor
  // a late flush can cross an internal identity boundary.
  sessionRecordingAllowed = allowed;

  try {
    if (allowed) {
      posthog.startSessionRecording();
    } else {
      posthog.stopSessionRecording();
    }
    return true;
  } catch {
    // A failed stop stays fail-closed at the final-send boundary. A failed
    // start restores the prior policy so replay is not reported as allowed.
    if (allowed) sessionRecordingAllowed = previousValue;
    captureBlocked = true;
    return false;
  }
}

export function componentPath(component: string) {
  if (!/^[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)*$/.test(component))
    return "/:unknown";

  const path = component
    .split("/")
    .map((segment) =>
      segment
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .replace(/_/g, "-")
        .toLowerCase(),
    )
    .join("/");

  return `/${path}`;
}

function resetIdentity() {
  let succeeded = true;
  try {
    posthog.reset();
    if (config) posthog.register(eventContext(config));
  } catch {
    captureBlocked = true;
    succeeded = false;
  } finally {
    identifiedUserId = null;
    identifiedTrafficClass = null;
    groupedOrganizationId = null;
    registeredTrafficClass = null;
  }
  return succeeded;
}

// `posthog.reset()` is how a PREVIOUS USER is forgotten. It is not a way to
// tidy up before identifying one, and using it as such is what broke every
// funnel that crossed sign-in.
//
// `identify()` emits its `$identify` merge event — the one carrying
// `$anon_distinct_id`, which is what joins the visitor's pre-sign-in events to
// the account — ONLY while the current distinct_id is still anonymous.
// `reset()` mints a brand new anonymous id, so resetting first discarded the
// exact id the merge existed to join: the anonymous person who viewed
// `auth/Login` was orphaned, and the account started life with no history.
//
// So a merely anonymous browser is never a reason to reset. Only a DIFFERENT
// person already attached to this browser is.
function attachedUserId() {
  // `_isIdentified()` reads the SDK's own `$user_state` — the same flag
  // `identify()` branches on. Comparing distinct_id against the incoming user
  // id cannot tell "a previous user" apart from "an anonymous visitor", and
  // treating the second as the first is the whole defect.
  return posthog._isIdentified() ? posthog.get_distinct_id() : null;
}

function syncIdentity(identity: AnalyticsIdentity | null | undefined) {
  // The SDK is the source of truth for who is attached: `identifiedUserId` is
  // only a cache of it, and starts null on every full page load while the
  // browser may still carry a persisted person.
  let priorUserId: string | null;
  try {
    priorUserId = attachedUserId() ?? identifiedUserId;
  } catch {
    captureBlocked = true;
    return false;
  }

  // Logout forgets a person. With nobody attached there is nothing to forget,
  // and resetting anyway would discard the anonymous id that a later sign-in
  // has to merge from.
  if (!identity) {
    if (priorUserId && !resetIdentity()) return false;
    return setSessionRecordingAllowed(true);
  }

  const excludeSessionRecording = usesWeftLabsEmail(identity);
  if (excludeSessionRecording) {
    if (!setSessionRecordingAllowed(false)) return false;

    // A sign-in normally merges the anonymous visitor into the account. For a
    // Weft Labs identity that would also attach its pre-sign-in replay. Start a
    // fresh anonymous identity after the recorder stops, then identify the
    // account from that replay-free session.
    if (!priorUserId && !resetIdentity()) return false;
  }

  if (priorUserId && priorUserId !== identity.user_id && !resetIdentity())
    return false;

  try {
    if (registeredTrafficClass !== identity.traffic_class) {
      posthog.register({ traffic_class: identity.traffic_class });
      registeredTrafficClass = identity.traffic_class;
    }
    if (
      identifiedUserId !== identity.user_id ||
      identifiedTrafficClass !== identity.traffic_class
    ) {
      // Only allowlisted person properties travel. Passing the whole identity
      // object would leak whatever Rails adds to it next.
      //
      // `traffic_class` rides here as well as on the events. The registered
      // super property is frozen into each event at ingest, so reclassifying an
      // account cannot retire the events it already produced; the PERSON
      // property is the account's current class, which is what a PostHog cohort
      // reads — so a reclassification retroactively excludes it.
      //
      // Which is why a CHANGED class re-identifies as well as a changed user:
      // guarding this on the user id alone would advance the super property
      // mid-session and leave the person profile on the old class until the
      // next full page load. A browser-only account has no server event to
      // carry the corrected `$set` in the meantime.
      const personProperties = {
        traffic_class: identity.traffic_class,
        ...(identity.email ? { email: identity.email } : {}),
      };
      posthog.identify(identity.user_id, personProperties);
      identifiedUserId = identity.user_id;
      identifiedTrafficClass = identity.traffic_class;
    }
    if (groupedOrganizationId !== identity.organization_id) {
      posthog.group("organization", identity.organization_id);
      groupedOrganizationId = identity.organization_id;
    }
    if (!excludeSessionRecording && !setSessionRecordingAllowed(true))
      return false;
    return true;
  } catch {
    captureBlocked = true;
    return false;
  }
}

export function trackPage(page: AnalyticsPage) {
  if (!initialized || captureBlocked || !config) return;

  if (!syncIdentity(page.props.analytics_identity) || captureBlocked) return;
  const pathname = componentPath(page.component);
  const routeComponent = pathname === "/:unknown" ? "unknown" : page.component;

  try {
    posthog.capture("$pageview", {
      $current_url: `${window.location.origin}${pathname}`,
      $pathname: pathname,
      route_component: routeComponent,
      ...(page.props.analytics_identity
        ? { traffic_class: page.props.analytics_identity.traffic_class }
        : {}),
      ...eventContext(config),
    });
  } catch {
    // Analytics is best effort and must never interrupt rendering.
  }
}

export function resetAnalytics() {
  if (!initialized) return;
  resetIdentity();
}
